/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */

import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import AggregateDataProvider from '../../network/AggregateDataProvider'
import AccountDataProvider from '../../network/AccountDataProvider'
import LedgerModalHost from '../modals/LedgerModalHost'
import TrezorModalHost from '../modals/TrezorModalHost'
import CoinomiModalHost from '../modals/CoinomiModalHost'
import InfinitoModalHost from '../modals/InfinitoModalHost'
import WalletConnectModal from '../modals/WalletConnectModal'
import { MiniTokenTradeModal } from '../modals/MiniTokenTradeModal'
import { GlobalStyle } from '../../components/mobile'
import IpValidation from '../../components/common/IpValidation'
import Header from '../header'
import TradingPair from './TradingPair'
import TradeHistory from './TradeHistory'
import Orderbook from './Orderbook'
import OrderbookMobile from '../mobile/OrderbookMobile'
import TradeOrders from './TradeOrders'
import PlaceOrder from './PlaceOrder'
import PlaceOrderMobile from '../mobile/PlaceOrderMobile'
import SymbolPriceInfo from './SymbolPriceInfo'
import ChartBox from './ChartBox'
import Confirm from '../../components/common/Confirm'
import { useI18n } from '../../i18n/util'
import isElectron from '../../utils/isElectron'
import { useLoadDepthChartLibraryEffect } from '../../hooks/useLoadDepthChartLibraryEffect'
import { setCurrentTrade, setShowPriceNotify, setPriceCheck } from '../../redux/exchange/action'
import { getDepth } from '../../redux/orderbooks/action'
import { isMobile, isPad } from '../../utils/bomUtils'
import { useCurrentTokenType, TOKEN_TYPE_BEP8 } from '../../utils/tokenTypes'
import { DexThemeProvider } from '../../components/common/DexThemeProvider'

import {
  Wrapper,
  ButtonTabs,
  ChartContent,
  ConfirmContent,
  Content,
  ContentCenter,
  ContentLeft,
  ContentRight,
  Footer,
  TabContent,
} from './styled'

const Exchange = () => {
  const [tab, setTab] = useState('Books')
  const { symbol } = useParams()
  const dispatch = useDispatch()
  const { push } = useHistory()
  const { locale, formatI18nText } = useI18n()
  const { currentTrade, showPriceNotify, priceNotifyText } = useSelector(state => state.exchange)
  const allPairs = useSelector(state => state.tradePairs.pairs)
  const triggerElement = useSelector(state => state.globalData.triggerElement)
  const tokenType = useCurrentTokenType()

  const filteredPairs = useMemo(() => allPairs.filter(it => it.tokenType === tokenType), [
    allPairs,
    tokenType,
  ])

  const confirm = useCallback(() => {
    dispatch(setShowPriceNotify(false))
    dispatch(setPriceCheck(true))
    triggerElement && triggerElement.click()
  }, [dispatch, triggerElement])

  const hidePriceNotify = useCallback(() => {
    dispatch(setShowPriceNotify(false))
    dispatch(setPriceCheck(false))
  }, [dispatch])

  const isCurrentTradeValid = useMemo(() => {
    if (filteredPairs.length <= 0) return true
    return !!filteredPairs.find(
      item => `${item.base_asset_symbol}_${item.quote_asset_symbol}` === currentTrade
    )
  }, [currentTrade, filteredPairs])

  useLoadDepthChartLibraryEffect()

  useEffect(() => {
    if (!symbol) return
    dispatch(setCurrentTrade(symbol))
    dispatch(getDepth({ symbol, limit: 50 }))
  }, [dispatch, symbol])

  useEffect(() => {
    if (currentTrade && !symbol) {
      dispatch(setCurrentTrade(currentTrade))
      dispatch(getDepth({ symbol: currentTrade, limit: 50 }))
      push(`/${locale}/trade/${tokenType === TOKEN_TYPE_BEP8 ? 'mini/' : ''}${currentTrade}`)
      return
    }

    if (
      (!symbol && !currentTrade && filteredPairs.length > 0) ||
      (currentTrade && !isCurrentTradeValid)
    ) {
      const defaultPair = filteredPairs[0]
      const firstTrade = `${defaultPair.base_asset_symbol}_${defaultPair.quote_asset_symbol}`
      dispatch(setCurrentTrade(firstTrade))
      dispatch(getDepth({ symbol: firstTrade, limit: 50 }))
      push(`/${locale}/trade/${tokenType === TOKEN_TYPE_BEP8 ? 'mini/' : ''}${firstTrade}`)
    }
  }, [dispatch, symbol, currentTrade, locale, push, filteredPairs, isCurrentTradeValid, tokenType])

  return (
    <DexThemeProvider theme={isElectron() ? 'light' : 'dark'}>
      <Fragment>
        <Wrapper>
          <Header withTradePairs={true} />
          <Content>
            {(!isMobile() || isPad()) && (
              <ContentLeft>
                <TradingPair />
                <TradeHistory />
              </ContentLeft>
            )}
            <ContentCenter>
              <SymbolPriceInfo />
              {isMobile() && (
                <ButtonTabs>
                  <div
                    className={tab === 'Books' ? 'tab on' : 'tab'}
                    onClick={() => setTab('Books')}
                  >
                    {formatI18nText('exchange.index.books')}
                  </div>
                  <div
                    className={tab === 'Chart' ? 'tab on' : 'tab'}
                    onClick={() => setTab('Chart')}
                  >
                    {formatI18nText('exchange.index.chart')}
                  </div>
                  <i className="tab-scrollbar" />
                </ButtonTabs>
              )}
              {isMobile() && (
                <TabContent show={tab === 'Books'}>
                  <OrderbookMobile />
                </TabContent>
              )}
              <ChartContent show={tab === 'Chart' || !isMobile()}>
                <ChartBox />
              </ChartContent>
              {isMobile() && <PlaceOrderMobile />}
            </ContentCenter>
            {(!isMobile() || isPad()) && (
              <ContentRight>
                <Orderbook />
              </ContentRight>
            )}
          </Content>
          {(!isMobile() || isPad()) && (
            <Footer>
              <TradeOrders />
              <PlaceOrder />
            </Footer>
          )}
        </Wrapper>
        {isMobile() && <GlobalStyle />}
        {showPriceNotify && (
          <Confirm
            cancelText={formatI18nText('common.cancel')}
            okText={formatI18nText('common.placeOrder')}
            confirm={confirm}
            cancel={hidePriceNotify}
          >
            <ConfirmContent>
              <p>{priceNotifyText}</p>
            </ConfirmContent>
          </Confirm>
        )}
        {/* Modal providers */}
        <LedgerModalHost showConfirmAddress={true} />
        <TrezorModalHost />
        <CoinomiModalHost />
        <InfinitoModalHost />
        <WalletConnectModal />
        {tokenType === TOKEN_TYPE_BEP8 && <MiniTokenTradeModal />}

        {/* WS provider */}
        <AggregateDataProvider />
        <AccountDataProvider />
        {!isElectron() && <IpValidation />}
      </Fragment>
    </DexThemeProvider>
  )
}

export default Exchange
